import Joi from '@hapi/joi';
import React from 'react';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchDelete, fetchGet } from '../../../store/fetch';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelRow,
    PanelBody,
    PanelButtonLink,
    PanelButtons,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../../elements';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import { ValidatingEditPanelSaveButton, ValidatingForm, ValidatingFormInputText } from '../../validatingElements';

interface ISurveyEditFmecaInspectionPlanAnalysisProps extends ITranslateProps {
    surveyId: string;
    objectId: string;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    failureModeId: string;
    selectedName?: undefined | string;
    selectedFailureModeUpdate: (selectedFailureModeId: string) => void;
}

interface IInspectionPlan {
    id: string;
    description: string;
}

interface ISurveyEditFmecaInspectionPlanAnalysisState {
    isLoaded: boolean;
    showAddEdit: boolean;
    selectedInspectionPlanId?: undefined | string;
    inspectionPlanList: IInspectionPlan[];
}

class SurveyEditFmecaInspectionPlanAnalysisComponent extends ComponentSetStatePromised<
    ISurveyEditFmecaInspectionPlanAnalysisProps,
    ISurveyEditFmecaInspectionPlanAnalysisState
> {
    constructor(props: ISurveyEditFmecaInspectionPlanAnalysisProps) {
        super(props);
        this.state = { isLoaded: false, showAddEdit: false, inspectionPlanList: [] };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override async componentDidUpdate(prevProps: ISurveyEditFmecaInspectionPlanAnalysisProps) {
        const shouldReload =
            prevProps.elementId !== this.props.elementId ||
            prevProps.unitId !== this.props.unitId ||
            prevProps.manifestationId !== this.props.manifestationId;

        if (shouldReload) {
            await this.setStatePromised({ isLoaded: false });
            await this.load();
        }
    }

    public override render() {
        if (!this.state.isLoaded) {
            return null;
        }

        return (
            <EditPanel bottomMargin={true} noPadding={true}>
                <CollapsiblePanel
                    heading={{ dark: true, text: `${this.props.t('survey.inspectionPlan')}: ${this.props.selectedName}`, initialIsClosed: true }}
                >
                    <PanelBody isForm={true}>
                        <PanelTable hover={true}>
                            <TableHead>
                                <TableHeadRow>
                                    <TableHeader>{this.props.t('common.description')}</TableHeader>
                                </TableHeadRow>
                            </TableHead>
                            <TableBody>
                                {this.state.inspectionPlanList.map((inspectionPlan) => (
                                    <TableRow
                                        key={inspectionPlan.id}
                                        isRowSelected={inspectionPlan.id === this.state.selectedInspectionPlanId}
                                        onClick={async () => this.selectInspectionPlan(inspectionPlan.id)}
                                    >
                                        <TableData>{inspectionPlan.description} </TableData>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </PanelTable>

                        <PanelButtons>
                            <PanelButtonLink
                                onClick={() => this.showAdd()}
                                text={this.props.t('common.addItem', this.props.t('survey.inspectionPlan'))}
                                icon="fa fa-plus"
                                small={true}
                                primary={true}
                            />
                        </PanelButtons>

                        {this.state.showAddEdit && (
                            <EditPanel>
                                <ValidatingForm
                                    schema={Joi.object()}
                                    createUrl={`/api/surveys/${this.props.surveyId}/inspectionPlans/`}
                                    createUrlQueryParameters={{ unitId: this.props.unitId, manifestationId: this.props.manifestationId }}
                                    itemUrl={`/api/surveys/${this.props.surveyId}/inspectionPlans/${this.state.selectedInspectionPlanId}`}
                                    id={this.state.selectedInspectionPlanId}
                                    onSubmitted={async () => this.handleSubmitted()}
                                >
                                    <EditPanelRow>
                                        <ValidatingFormInputText name="description" placeholder={this.props.t('common.description')} />
                                    </EditPanelRow>

                                    <EditPanelButtons>
                                        {this.state.selectedInspectionPlanId && (
                                            <EditPanelButton onClick={async () => this.remove()} text={this.props.t('common.remove')} small={true} />
                                        )}
                                        <EditPanelButton
                                            onClick={() => this.setState({ showAddEdit: false })}
                                            text={this.props.t('common.cancel')}
                                            small={true}
                                        />
                                        <ValidatingEditPanelSaveButton small={true} />
                                    </EditPanelButtons>
                                </ValidatingForm>
                            </EditPanel>
                        )}
                    </PanelBody>
                </CollapsiblePanel>
            </EditPanel>
        );
    }

    private async selectInspectionPlan(id: string) {
        await this.setStatePromised({ selectedInspectionPlanId: id, showAddEdit: true });
    }

    private async handleSubmitted() {
        await this.setStatePromised({ showAddEdit: false });
        await this.load();
    }

    private showAdd() {
        this.setState({ showAddEdit: true, selectedInspectionPlanId: undefined });
    }

    private async load() {
        const url = `/api/surveys/${this.props.surveyId}/inspectionPlans/`;
        const result = await fetchGet(url, { unitId: this.props.unitId, manifestationId: this.props.manifestationId });
        await this.setStatePromised({ inspectionPlanList: result.json.items, isLoaded: true });
    }

    private async remove() {
        await fetchDelete(`/api/surveys/${this.props.surveyId}/inspectionPlans/${this.state.selectedInspectionPlanId}`);
        await this.setStatePromised({ showAddEdit: false, selectedInspectionPlanId: undefined });
        await this.load();
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyEditFmecaInspectionPlanAnalysis = translate()(SurveyEditFmecaInspectionPlanAnalysisComponent);
